import { Inject, Injectable } from '@angular/core';
import { getDate8AsInt, getId, isset, sameId } from '@cawita/core-front';
import { CwtApiService, mapApiDataItem, mapPaginatedToArray, RequestConfig } from '@cawita/core-front/api';
import { CustomizationSettings, Event, Partner, Prestation, Stay, Tip } from '@shared/models';
import { GenericCrudService, getCrudName } from '@shared/modules/crud';
import { forkJoin, Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';

export type PartnerMetadata = {
  settings: CustomizationSettings;
  event: Event;
  prestation: Prestation;
  tip: Tip;
}

@Injectable({ providedIn: 'root' })
export class PartnerAPI {
    constructor(
        private api: CwtApiService,
        @Inject(getCrudName(Partner)) private partnerCrud: GenericCrudService<Partner>,
        @Inject(getCrudName(Event)) protected eventCrud: GenericCrudService<Event>,
        @Inject(getCrudName(Prestation)) protected prestationCrud: GenericCrudService<Prestation>,
        @Inject(getCrudName(Tip)) protected tipCrud: GenericCrudService<Tip>,
        @Inject(getCrudName(CustomizationSettings)) private customizationSettingsCrud: GenericCrudService<Partner>,
    ) { }

    public getPartner(partnerId: string) {
        return this.partnerCrud.getOne(partnerId, {
            bypassCache: true,
            options: {
                projection: 'forAppForUser'
            }
        });
    }

    public getPartnerDocument(partnerId: string, documentId: string) {
        return this.getPartner(partnerId).pipe(
            map(res => res.documents?.find(doc => sameId(doc, documentId)))
        );
    }


    public getPartnerForCode(code: string, config?: RequestConfig) {
        return this.api.get(`stayCreationCode/${code}`, {}, {
            ...config,
            bypassCache: true,
        }).pipe(
            map((res) => isset(res) ? res : { camping: null }),
            pluck('camping'),
            mapApiDataItem(Partner)
        );
    }

    public getPartnerCustomizationSettings(partnerId: string) {
        return this.customizationSettingsCrud.getAllNoPaginate({ partner: partnerId }, { bypassCache: true }).pipe(
            map(settings => settings[0]),
            map(settings => this._wrapWithDefaults(settings))
        );
    }

    private _wrapWithDefaults(settings?: CustomizationSettings) {
        return new CustomizationSettings({
            ...(settings ?? {}),
            stayCardInfo: settings?.stayCardInfo,
            enjoyCardInfo: settings?.enjoyCardInfo,
            discoverCardInfo: settings?.discoverCardInfo,
        });
    }

    public getMetadata(partner: Partner, stay?: Stay): Observable<PartnerMetadata> {
        const startDate = this._getMetaStartDate(stay);
        return forkJoin([
            this.getPartnerCustomizationSettings(getId(partner)),
            this.eventCrud.getAllPaginated({ partner: getId(partner), startDate }, { options: { projection: 'forEventListForApp', limit: 1, sort: 'startDate' } }).pipe(mapPaginatedToArray(), pluck(0)),
            this.prestationCrud.getAllPaginated({ partners: getId(partner) }, { options: { limit: 1 } }).pipe(mapPaginatedToArray(), pluck(0)),
            this.tipCrud.getAllPaginated({ partners: getId(partner) }, { options: { limit: 1, sort: '-startDate' } }).pipe(mapPaginatedToArray(), pluck(0)),
        ]).pipe(map(([settings, event, prestation, tip]) => ({
          settings,
          event,
          prestation,
          tip
        })))
    }
    
    private _getMetaStartDate(stay?: Stay) {
      if(!isset(stay)) return getDate8AsInt(new Date()) * 10000;
      return Math.max(getDate8AsInt(new Date()), stay.startDate) * 10000
    }
}